import { BarChart3, Briefcase, Building2, Calculator, CalendarClock, ClipboardCheck, Coins, CreditCard, DatabaseBackup, FileText, FileWarning, Gauge, Gavel, Gift, Home, Layers, LayoutGrid, ListChecks, LogOut, MapPinned, Megaphone, Plug, Receipt, Settings, ShieldCheck, ShoppingBasket, ShoppingCart, Users, Warehouse } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useFeatures } from "../../hooks/useFeatures";
import { IS_STORE_BUILD, PURCHASE_NAV_KEYS } from "../../config/storeBuild";
import { ADMIN_ROLES, MANAGER_ROLES, ALL_ROLES } from "../../utils/role";

const ALL = ALL_ROLES;
const MANAGERS = MANAGER_ROLES;
const ADMINS = ADMIN_ROLES;

// the dashboard lives inside the home page now, so it is not a separate destination
export const ITEMS = [
  { to: "/", key: "home", icon: Home, roles: ALL },
  { to: "/my-log", key: "myLog", icon: CalendarClock, roles: ALL },
  { to: "/my-payslip", key: "myPayslip", icon: Receipt, roles: ALL },
  { to: "/my-performance", key: "myPerformance", icon: Gauge, roles: ALL, feature: "kpi" },
  { to: "/my-requests", key: "myRequests", icon: FileText, roles: ALL },
  { to: "/tasks", key: "tasks", icon: ListChecks, roles: ALL },
  { to: "/resignation", key: "resignation", icon: LogOut, roles: ALL },
  { to: "/employees", key: "employees", icon: Users, roles: MANAGERS },
  { to: "/documents", key: "documents", icon: FileWarning, roles: ADMINS },
  { to: "/requests", key: "requests", icon: ClipboardCheck, roles: ADMINS },
  { to: "/accounting", key: "accounting", icon: Calculator, roles: MANAGERS, feature: "accounting" },
  { to: "/sales", key: "sales", icon: ShoppingCart, roles: MANAGERS, feature: "sales" },
  { to: "/purchases", key: "purchases", icon: ShoppingBasket, roles: MANAGERS, feature: "purchases" },
  { to: "/warehouse", key: "warehouse", icon: Warehouse, roles: MANAGERS, feature: "warehouse" },
  { to: "/duties", key: "duties", icon: Briefcase, roles: MANAGERS, feature: "duties" },
  { to: "/branches", key: "branches", icon: Building2, roles: ADMINS },
  { to: "/departments", key: "departments", icon: Layers, roles: ADMINS },
  { to: "/live-map", key: "liveMap", icon: MapPinned, roles: MANAGERS, feature: "live_map" },
  { to: "/penalties", key: "penalties", icon: Gavel, roles: MANAGERS, feature: "penalties" },
  { to: "/kpi", key: "kpi", icon: Gauge, roles: MANAGERS, feature: "kpi" },
  { to: "/bonuses", key: "bonuses", icon: Gift, roles: MANAGERS, feature: "bonuses" },
  { to: "/my-bonuses", key: "myBonuses", icon: Gift, roles: ALL, feature: "bonuses" },
  { to: "/reports", key: "reports", icon: BarChart3, roles: MANAGERS },
  { to: "/wallet", key: "wallet", icon: Coins, roles: MANAGERS },
  { to: "/payroll", key: "payroll", icon: Receipt, roles: ADMINS },
  { to: "/permissions", key: "permissions", icon: ShieldCheck, roles: ADMINS },
  { to: "/integrations", key: "api", icon: Plug, roles: ADMINS, feature: "api_webhooks" },
  { to: "/backup", key: "backup", icon: DatabaseBackup, roles: ADMINS, feature: "company_backup" },
  { to: "/company-notify", key: "companyNotify", icon: Megaphone, roles: MANAGERS, feature: "company_broadcast" },
  { to: "/billing", key: "billing", icon: CreditCard, roles: ADMINS },
  { to: "/settings", key: "settings", icon: Settings, roles: ALL },
];

// keys shown in the floating bar (home first, then the rest); everything else lives as home tiles
const BOTTOM_ORDER = ["home", "myPayslip", "myRequests", "more"];
// "المزيد": عنصر افتراضي يفتح صفحة تضم كل الاختصارات والإعدادات.
const MORE_ITEM = { to: "/more", key: "more", icon: LayoutGrid, roles: ALL };

export const useNavItems = () => {
  const { user } = useAuth();
  const features = useFeatures();
  const allowed = ITEMS.filter(
    (i) =>
      i.roles.includes(user?.role) &&
      (!i.feature || features[i.feature]) &&
      !(IS_STORE_BUILD && PURCHASE_NAV_KEYS.includes(i.key)),
  );
  const bottom = BOTTOM_ORDER.map((key) => (key === "more" ? MORE_ITEM : allowed.find((i) => i.key === key)))
    .filter(Boolean).slice(0, 4);
  const bottomKeys = bottom.map((i) => i.key);
  const tiles = allowed.filter((i) => i.key !== "home" && !bottomKeys.includes(i.key));
  // على الكمبيوتر يختفي الشريط السفلي، فنعرض عناصره (عدا الرئيسية و"المزيد") كاختصارات للكمبيوتر فقط.
  const bottomExtra = bottom.filter((i) => i.key !== "home" && i.key !== "more").map((i) => ({ ...i, desktopOnly: true }));
  const quickTiles = [...bottomExtra, ...tiles];
  return { allowed, bottom, tiles, quickTiles };
};
